// Initialize sentry before everything
import './sentry';

// Load polyfills
import 'blueimp-canvas-to-blob';

// Load other libraries
import createMetaNavigationGuard from '@evidentid/vue-commons/router/createMetaNavigationGuard';

import idoApplication from '@/idoApplication';
import { bootstrapWeglot } from '@/services/weglot';
import { useIdoStore } from '@/store/store';
import createEventHandlerForBlockingRefreshDuringSubmission
    from '@/utils/createEventHandlerForBlockingRefreshDuringSubmission';

// PRODUCT-26110 Phase 2B2: `Vue.config.productionTip` was removed outright in Vue 3
// (dead option, no compat shim) -- matches the identical fix already landed in
// tprm-portal's main.ts (ed2202df8), deleted rather than cast.

// Incode Web SDK renders <incode-flow> as a custom element (web component).
// PRODUCT-26110 Task 12b (CONFIG_IGNORED_ELEMENTS): moved to the real Vue 3
// replacement, the `@vitejs/plugin-vue` `compilerOptions.isCustomElement`
// (vite.config.mts) -- a build/compiler-config change, not a runtime call.
// `Vue.config.ignoredElements`
// itself is removed outright now that CONFIG_IGNORED_ELEMENTS is flipped off (it was
// tracked in dashboard-commons' compatConfig.ts, since deleted along with @vue/compat
// in PRODUCT-26135); the compiler-level `isCustomElement` check makes the runtime
// config entirely unnecessary.

// PRODUCT-26110 Task 13 (Finding 3): vue-axe@2.x (dev-only a11y auditor, never shipped)
// is Vue-2-only -- its `install(Vue, opts)` calls the *global* `Vue.mixin({ updated,
// beforeDestroy })` and sets `Vue.prototype.$axe`, both v2-only global-singleton APIs
// that MODE 3 left disabled (GLOBAL_MIXIN/GLOBAL_PROTOTYPE weren't in the re-enabled
// set of the since-deleted compatConfig.ts, and `beforeDestroy` itself was a dead hook
// name under OPTIONS_BEFORE_DESTROY: false) -- so under MODE 3 this integration was
// either inert or would have thrown on the `Vue.mixin(...)` call, and was crashing
// dev serve per this task's browser QA. There IS a Vue-3-native replacement
// (`vue-axe@next`, v3.x, `peerDependencies: { vue: ^3 }`), but it is not a drop-in
// swap: v3 requires calling `app.use(VueAxe.default, ...)` against the real
// `createApp()` instance (this file
// only has the legacy global `Vue` singleton in scope -- `idoApplication` doesn't
// expose the underlying app), requires mounting a `<VueAxePopup>` component into the
// actual render tree to see violations, and reshapes the options object (`config` vs.
// `runOptions.runOnly`). That's a real feature addition (app-bootstrap plumbing + a
// rendered UI), not a crash fix, and out of scope for this MODE-3 regression burndown.
// Removing the integration outright is the correct-sized fix here: it's dev-only
// tooling, never shipped, and its absence doesn't regress anything users see. If a11y
// auditing is wanted going forward, wire up vue-axe@next against the app instance as
// its own follow-up task.

// PRODUCT-26110 Task 8b Step 2: PortalVue install removed -- Modal.vue/ModalTarget.vue
// were migrated to native <Teleport>, which needs no plugin registration.

if (idoApplication.router) {
    idoApplication.router.beforeEach(createMetaNavigationGuard('Evident', document));
}

// Find 'root' element
const rootElement = document.querySelector('#root');

// Mount application
if (rootElement) {
    idoApplication.mount(rootElement);
} else {
    throw new Error('Unable to find #root element.');
}

// After mount, so Weglot's observer has the rendered tree to translate, and after
// the application's own initialize(), which normalizes legacy hash URLs first.
// Deliberately not awaited: nothing below depends on translation, and it resolves
// to false rather than rejecting when Weglot is unavailable.
void bootstrapWeglot(window.location);

if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line
    // @ts-ignore: attaching application instance to window
    window.idoApp = idoApplication;
}

// Side effect: ensure that user will not leave during submission.
// Unfortunately, Safari on iOS is not supporting it.
window.onbeforeunload = createEventHandlerForBlockingRefreshDuringSubmission(useIdoStore(idoApplication.pinia));
